import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { SEGMENTOS } from '../dados/segmentos';
import { CamposLocalidade } from '../components/CamposLocalidade';
import { gerarSenha, mensagemDeAcesso } from '../dados/mensagem-acesso';
import {
  Campo,
  Carregando,
  ErroBox,
  Form,
  LinhaCampos,
  Modal,
  ModalRodape,
} from '../components/ui';
import { limpar } from '../pages/Eventos';

interface Detalhe {
  id: string;
  legalName: string;
  tradeName: string;
  country: string;
  state: string | null;
  city: string | null;
  segment: string | null;
  status: string;
  createdAt: string;
  internalNotes?: string | null;
}

interface UsuarioDoCliente {
  id: string;
  name: string;
  email: string;
  status: string;
  lastLoginAt: string | null;
  createdAt: string;
  roles: { role: { slug: string; name: string } }[];
}

interface UsuarioCriado {
  user: { id: string; name: string; email: string; status: string };
  /** Nulo quando a senha foi definida na criação. */
  inviteToken: string | null;
  /** Existe só nesta tela, entre criar e copiar a mensagem. */
  senha?: string;
}

const PAPEIS = [
  { slug: 'admin-organizacao', nome: 'Administrador da Organização' },
  { slug: 'gestor-evento', nome: 'Gestor de Evento' },
  { slug: 'operador', nome: 'Operador' },
  { slug: 'analista', nome: 'Analista' },
  { slug: 'visualizador', nome: 'Visualizador' },
];

/**
 * Dois estados, não quatro.
 *
 * O banco ainda conhece `SUSPENSA` e `BLOQUEADA`, mas as três opções fora de
 * ATIVA faziam exatamente a mesma coisa — e três palavras para um efeito só
 * viram três interpretações diferentes na hora de escolher. Um cliente antigo
 * gravado num dos valores aposentados continua aparecendo, senão o campo
 * mostraria um estado que não é o dele.
 */
function statusPossiveis(atual: string) {
  const oferecidos = ['ATIVA', 'INATIVA'];
  return oferecidos.includes(atual) ? oferecidos : [...oferecidos, atual];
}

/**
 * Painel de administração de um cliente.
 *
 * Três abas porque são três naturezas diferentes de risco: dados cadastrais
 * (reversível), equipe (tira gente do sistema) e exclusão (derruba tudo). A
 * última fica separada e por último, para não ser clicada de passagem.
 */
export function ConfigurarCliente({ id, aoFechar }: { id: string; aoFechar: () => void }) {
  const cliente = useQueryClient();
  const [aba, setAba] = useState<'geral' | 'equipe' | 'perigo'>('geral');
  const [confirmacao, setConfirmacao] = useState('');
  // A senha vive só aqui, entre definir e copiar. Ao fechar o modal ela some da
  // tela — do servidor nunca saiu, ele guarda o hash.
  const [senhaDe, setSenhaDe] = useState<UsuarioDoCliente | null>(null);
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmaSenha, setConfirmaSenha] = useState('');
  const [senhaDefinida, setSenhaDefinida] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);

  const [criandoUsuario, setCriandoUsuario] = useState(false);
  const [criado, setCriado] = useState<UsuarioCriado | null>(null);

  const org = useQuery({
    queryKey: ['organizacao', id],
    queryFn: () => api<Detalhe>(`/organizations/${id}`),
  });

  const equipe = useQuery({
    queryKey: ['organizacao', id, 'usuarios'],
    queryFn: () => api<UsuarioDoCliente[]>(`/organizations/${id}/users`),
    enabled: aba === 'equipe',
  });

  const invalidar = () => {
    cliente.invalidateQueries({ queryKey: ['organizacao', id] });
    cliente.invalidateQueries({ queryKey: ['organizacoes'] });
  };

  const salvar = useMutation({
    mutationFn: (dados: Record<string, string>) =>
      api(`/organizations/${id}`, { method: 'PATCH', body: limpar(dados) }),
    onSuccess: invalidar,
  });

  const alterarUsuario = useMutation({
    mutationFn: ({ userId, ...body }: { userId: string } & Record<string, string>) =>
      api(`/organizations/${id}/users/${userId}`, { method: 'PATCH', body }),
    onSuccess: invalidar,
  });

  /**
   * Redefinição de senha — mutação separada da de papel e status porque o
   * sucesso dela não é "salvou": é a mensagem que ainda precisa ser copiada
   * antes de a janela fechar.
   */
  const definirSenha = useMutation({
    mutationFn: (password: string) =>
      api(`/organizations/${id}/users/${senhaDe?.id}`, {
        method: 'PATCH',
        body: { password },
      }),
    onSuccess: (_resposta, password) => {
      invalidar();
      setSenhaDefinida(password);
    },
  });

  const criarUsuario = useMutation({
    mutationFn: (campos: Record<string, string>) =>
      api<UsuarioCriado>(`/organizations/${id}/users`, {
        method: 'POST',
        body: {
          name: campos.name,
          email: campos.email,
          roleSlug: campos.roleSlug,
          // Em branco significa "manda convite" — string vazia seria recusada
          // pelo `min(10)` do servidor.
          ...(novaSenha ? { password: novaSenha } : {}),
        },
      }),
    onSuccess: (resposta) => {
      invalidar();
      // Lida antes de o formulário fechar: depois disso a senha não existe em
      // lugar nenhum, nem aqui nem no servidor.
      setCriado({ ...resposta, senha: novaSenha || undefined });
      setNovaSenha('');
      setConfirmaSenha('');
    },
  });

  function fecharCriacao() {
    setCriandoUsuario(false);
    setCriado(null);
    setNovaSenha('');
    setConfirmaSenha('');
    setCopiado(false);
    criarUsuario.reset();
  }

  function fecharSenha() {
    setSenhaDe(null);
    setNovaSenha('');
    setConfirmaSenha('');
    setSenhaDefinida(null);
    setCopiado(false);
    definirSenha.reset();
  }

  async function copiar(texto: string) {
    await navigator.clipboard.writeText(texto);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  // Comparada enquanto se digita: descobrir a divergência só no envio é o pior
  // momento para descobrir.
  const erroSenha =
    confirmaSenha && novaSenha !== confirmaSenha
      ? 'As senhas não conferem.'
      : novaSenha && novaSenha.length < 10
        ? 'Use ao menos 10 caracteres.'
        : undefined;

  const removerUsuario = useMutation({
    mutationFn: (userId: string) =>
      api(`/organizations/${id}/users/${userId}`, { method: 'DELETE' }),
    onSuccess: invalidar,
  });

  const excluir = useMutation({
    mutationFn: () =>
      api<{ usuariosRemovidos: number }>(`/organizations/${id}`, {
        method: 'DELETE',
        body: { confirmacao },
      }),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: ['organizacoes'] });
      aoFechar();
    },
  });

  const dados = org.data;
  const confere =
    !!dados && confirmacao.trim().toLowerCase() === dados.tradeName.trim().toLowerCase();

  const painel = (
    <Modal
      titulo={dados ? `Configurar ${dados.tradeName}` : 'Configurar cliente'}
      descricao={dados?.legalName}
      aoFechar={aoFechar}
      largura={680}
    >
      <div className="abas">
        <button className={`aba ${aba === 'geral' ? 'ativa' : ''}`} onClick={() => setAba('geral')}>
          <i className="ti ti-building" /> Informações
        </button>
        <button
          className={`aba ${aba === 'equipe' ? 'ativa' : ''}`}
          onClick={() => setAba('equipe')}
        >
          <i className="ti ti-users" /> Equipe
        </button>
        <button
          className={`aba ${aba === 'perigo' ? 'ativa' : ''}`}
          onClick={() => setAba('perigo')}
        >
          <i className="ti ti-alert-triangle" /> Excluir
        </button>
      </div>

      {org.isPending && <Carregando />}
      {org.isError && <ErroBox erro={org.error} aoTentar={() => org.refetch()} />}

      {dados && aba === 'geral' && (
        <Form aoEnviar={(d) => salvar.mutate(d)}>
          {salvar.isError && <div className="form-erro">{(salvar.error as Error).message}</div>}
          {salvar.isSuccess && (
            <div className="aviso" style={{ marginBottom: 16 }}>
              <i className="ti ti-check" />
              <span>Alterações salvas.</span>
            </div>
          )}

          <LinhaCampos>
            <Campo label="Razão social" name="legalName" valorInicial={dados.legalName} />
            <Campo label="Nome fantasia" name="tradeName" valorInicial={dados.tradeName} />
          </LinhaCampos>

          <LinhaCampos>
            <Campo
              label="Segmento"
              name="segment"
              valorInicial={dados.segment ?? ''}
              opcoes={[
                { valor: '', rotulo: 'Selecione…' },
                ...SEGMENTOS.map((s) => ({ valor: s, rotulo: s })),
              ]}
            />
            <Campo
              label="Status"
              name="status"
              valorInicial={dados.status}
              opcoes={statusPossiveis(dados.status).map((s) => ({ valor: s, rotulo: s }))}
              ajuda="INATIVA desconecta todo mundo do cliente e impede novo login."
            />
          </LinhaCampos>

          <CamposLocalidade
            paisInicial={dados.country}
            estadoInicial={dados.state ?? ''}
            cidadeInicial={dados.city ?? ''}
          />

          <div className="divider" style={{ margin: '4px 0 18px' }} />

          <Campo
            label="Notas internas"
            name="internalNotes"
            linhas={3}
            valorInicial={dados.internalNotes ?? ''}
            ajuda="Só a MatériaPrimAi vê. O cliente nunca."
          />

          <ModalRodape>
            <button type="button" className="btn btn-secondary" onClick={aoFechar}>
              Fechar
            </button>
            <button className="btn btn-primary" type="submit" disabled={salvar.isPending}>
              {salvar.isPending ? 'Salvando…' : 'Salvar alterações'}
            </button>
          </ModalRodape>
        </Form>
      )}

      {aba === 'equipe' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button className="btn btn-primary btn-sm" onClick={() => setCriandoUsuario(true)}>
              <i className="ti ti-user-plus" /> Adicionar pessoa
            </button>
          </div>

          {equipe.isPending && <Carregando />}
          {equipe.isError && <ErroBox erro={equipe.error} aoTentar={() => equipe.refetch()} />}
          {alterarUsuario.isError && (
            <div className="form-erro">{(alterarUsuario.error as Error).message}</div>
          )}
          {removerUsuario.isError && (
            <div className="form-erro">{(removerUsuario.error as Error).message}</div>
          )}

          {equipe.data && (
            <div className="tabela-envolvente">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Pessoa</th>
                    <th title="Trocar o papel desconecta a pessoa; ela entra de novo já com o acesso novo.">
                      Papel
                    </th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {equipe.data.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <div className="celula-principal">{u.name}</div>
                        <div className="celula-secundaria">{u.email}</div>
                      </td>
                      <td>
                        <select
                          className="form-select"
                          style={{ width: 'auto', padding: '4px 8px', fontSize: 12 }}
                          value={u.roles[0]?.role.slug ?? ''}
                          disabled={alterarUsuario.isPending}
                          onChange={(e) =>
                            alterarUsuario.mutate({ userId: u.id, roleSlug: e.target.value })
                          }
                        >
                          {PAPEIS.map((p) => (
                            <option key={p.slug} value={p.slug}>
                              {p.nome}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className="form-select"
                          style={{ width: 'auto', padding: '4px 8px', fontSize: 12 }}
                          value={u.status === 'CONVIDADO' ? '' : u.status}
                          disabled={alterarUsuario.isPending}
                          onChange={(e) =>
                            alterarUsuario.mutate({ userId: u.id, status: e.target.value })
                          }
                        >
                          {u.status === 'CONVIDADO' && <option value="">CONVIDADO</option>}
                          {['ATIVO', 'INATIVO', 'BLOQUEADO'].map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <div className="acoes-linha">
                          <button
                            className="btn btn-secondary btn-sm"
                            title="Definir nova senha"
                            onClick={() => setSenhaDe(u)}
                          >
                            <i className="ti ti-key" />
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            title="Remover do cliente"
                            disabled={removerUsuario.isPending}
                            onClick={() => {
                              if (confirm(`Remover ${u.name}? Ele perde o acesso na hora.`)) {
                                removerUsuario.mutate(u.id);
                              }
                            }}
                          >
                            <i className="ti ti-trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <ModalRodape>
            <button className="btn btn-secondary" onClick={aoFechar}>
              Fechar
            </button>
          </ModalRodape>
        </>
      )}

      {dados && aba === 'perigo' && (
        <>
          <div className="aviso" style={{ marginBottom: 16, borderColor: 'var(--danger)' }}>
            <i className="ti ti-alert-triangle" style={{ color: 'var(--danger)' }} />
            <span>
              Excluir <strong>{dados.tradeName}</strong> desativa a organização e{' '}
              <strong>todos os usuários dela</strong>, encerrando as sessões abertas na hora.
              Ninguém do cliente volta a entrar.
            </span>
          </div>

          <p style={{ color: 'var(--text-muted)', fontSize: 13, lineHeight: 1.6 }}>
            Os dados <strong>não são apagados do banco</strong>: eventos, mensagens e o rastro
            de auditoria continuam existindo, porque a LGPD exige poder responder depois quem
            fez o quê. O cliente some da plataforma, não da história.
          </p>

          <label className="campo" style={{ marginTop: 16 }}>
            <span className="campo-label">
              Digite <strong>{dados.tradeName}</strong> para confirmar
            </span>
            <input
              className="form-input"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              placeholder={dados.tradeName}
            />
          </label>

          {excluir.isError && <div className="form-erro">{(excluir.error as Error).message}</div>}

          <ModalRodape>
            <button className="btn btn-secondary" onClick={aoFechar}>
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              style={{ background: 'var(--danger)', borderColor: 'var(--danger)' }}
              disabled={!confere || excluir.isPending}
              onClick={() => excluir.mutate()}
            >
              {excluir.isPending ? 'Excluindo…' : 'Excluir cliente'}
            </button>
          </ModalRodape>
        </>
      )}
    </Modal>
  );

  const mensagem =
    dados && senhaDe && senhaDefinida
      ? mensagemDeAcesso({
          cliente: dados.tradeName,
          email: senhaDe.email,
          senha: senhaDefinida,
          redefinida: true,
        })
      : '';

  const mensagemDoNovo =
    dados && criado
      ? mensagemDeAcesso({
          cliente: dados.tradeName,
          email: criado.user.email,
          senha: criado.senha,
          inviteToken: criado.inviteToken,
        })
      : '';

  // O modal da senha é IRMÃO do painel, não filho: dentro dele, clicar no fundo
  // escuro de cima borbulharia até o fundo de baixo e fecharia os dois de uma
  // vez — e o `.modal-corpo` rola, o que atrapalharia uma sobreposição.
  return (
    <>
      {painel}

      {senhaDe && (
        <Modal
          titulo={senhaDefinida ? 'Senha definida' : 'Definir senha'}
          descricao={`${senhaDe.name} · ${senhaDe.email}`}
          aoFechar={fecharSenha}
          largura={520}
        >
          {senhaDefinida ? (
            <>
              <div className="aviso" style={{ marginBottom: 16 }}>
                <i className="ti ti-alert-triangle" />
                <span>
                  Copie agora: ao fechar esta janela a senha some. O servidor guarda só o hash.
                </span>
              </div>

              <label className="campo">
                <span className="campo-label">Mensagem para {senhaDe.name}</span>
                <textarea
                  className="form-input mono"
                  readOnly
                  rows={8}
                  value={mensagem}
                  style={{ resize: 'vertical', lineHeight: 1.6 }}
                  onFocus={(e) => e.target.select()}
                />
              </label>

              <ModalRodape>
                <button className="btn btn-secondary" onClick={() => void copiar(mensagem)}>
                  <i className={`ti ${copiado ? 'ti-check' : 'ti-copy'}`} />
                  {copiado ? 'Copiado' : 'Copiar mensagem'}
                </button>
                <button className="btn btn-primary" onClick={fecharSenha}>
                  Fechar
                </button>
              </ModalRodape>
            </>
          ) : (
            <Form aoEnviar={() => definirSenha.mutate(novaSenha)}>
              <div className="aviso" style={{ marginBottom: 16 }}>
                <i className="ti ti-info-circle" />
                <span>
                  A senha nova vale na hora e <strong>derruba as sessões abertas</strong> desta
                  pessoa. Um convite pendente, se houver, deixa de funcionar.
                </span>
              </div>

              {definirSenha.isError && (
                <div className="form-erro">{(definirSenha.error as Error).message}</div>
              )}

              <LinhaCampos>
                <Campo
                  label="Nova senha"
                  name="novaSenha"
                  tipo="password"
                  obrigatorio
                  autoFocus
                  valor={novaSenha}
                  aoMudar={setNovaSenha}
                  ajuda="Ao menos 10 caracteres."
                />
                <Campo
                  label="Confirmar senha"
                  name="confirmaSenha"
                  tipo="password"
                  obrigatorio
                  valor={confirmaSenha}
                  aoMudar={setConfirmaSenha}
                  erro={erroSenha}
                />
              </LinhaCampos>

              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  const gerada = gerarSenha();
                  setNovaSenha(gerada);
                  setConfirmaSenha(gerada);
                }}
              >
                <i className="ti ti-wand" /> Gerar senha
              </button>

              <ModalRodape>
                <button type="button" className="btn btn-secondary" onClick={fecharSenha}>
                  Cancelar
                </button>
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={novaSenha.length < 10 || !!erroSenha || definirSenha.isPending}
                >
                  {definirSenha.isPending ? 'Definindo…' : 'Definir senha'}
                </button>
              </ModalRodape>
            </Form>
          )}
        </Modal>
      )}

      {criandoUsuario && (
        <Modal
          titulo={criado ? 'Pessoa adicionada' : 'Adicionar pessoa'}
          descricao={
            criado
              ? criado.inviteToken
                ? `Envie o link abaixo para ${criado.user.name} definir a senha.`
                : `${criado.user.name} já pode entrar com a senha que você definiu.`
              : dados?.tradeName
          }
          aoFechar={fecharCriacao}
          largura={560}
        >
          {criado ? (
            <>
              <div className="aviso" style={{ marginBottom: 16 }}>
                <i className="ti ti-alert-triangle" />
                <span>
                  {criado.senha
                    ? 'Copie agora: ao fechar esta janela a senha some. O servidor guarda só o hash.'
                    : 'Copie agora: o link aparece uma única vez — o servidor guarda só o hash.'}
                </span>
              </div>

              <label className="campo">
                <span className="campo-label">Mensagem para {criado.user.name}</span>
                <textarea
                  className="form-input mono"
                  readOnly
                  rows={criado.senha ? 8 : 7}
                  value={mensagemDoNovo}
                  style={{ resize: 'vertical', lineHeight: 1.6 }}
                  onFocus={(e) => e.target.select()}
                />
              </label>

              <ModalRodape>
                <button className="btn btn-secondary" onClick={() => void copiar(mensagemDoNovo)}>
                  <i className={`ti ${copiado ? 'ti-check' : 'ti-copy'}`} />
                  {copiado ? 'Copiado' : 'Copiar mensagem'}
                </button>
                <button className="btn btn-primary" onClick={fecharCriacao}>
                  Fechar
                </button>
              </ModalRodape>
            </>
          ) : (
            <Form aoEnviar={(campos) => criarUsuario.mutate(campos)}>
              {criarUsuario.isError && (
                <div className="form-erro">{(criarUsuario.error as Error).message}</div>
              )}

              <LinhaCampos>
                <Campo label="Nome" name="name" obrigatorio autoFocus />
                <Campo label="E-mail" name="email" tipo="email" obrigatorio />
              </LinhaCampos>

              <Campo
                label="Papel"
                name="roleSlug"
                obrigatorio
                // Operador é o papel de quem toca o dia a dia do evento — o
                // caso comum. Admin da organização é escolha consciente.
                valorInicial="operador"
                opcoes={PAPEIS.map((p) => ({ valor: p.slug, rotulo: p.nome }))}
                ajuda="Define o que a pessoa enxerga e pode fazer dentro do cliente."
              />

              <LinhaCampos>
                <Campo
                  label="Senha"
                  name="senhaNova"
                  tipo="password"
                  valor={novaSenha}
                  aoMudar={setNovaSenha}
                  ajuda="Em branco = a pessoa define a própria senha por convite."
                />
                <Campo
                  label="Confirmar senha"
                  name="senhaConfirma"
                  tipo="password"
                  valor={confirmaSenha}
                  aoMudar={setConfirmaSenha}
                  erro={erroSenha}
                />
              </LinhaCampos>

              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  const gerada = gerarSenha();
                  setNovaSenha(gerada);
                  setConfirmaSenha(gerada);
                }}
              >
                <i className="ti ti-wand" /> Gerar senha
              </button>

              <ModalRodape>
                <button type="button" className="btn btn-secondary" onClick={fecharCriacao}>
                  Cancelar
                </button>
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={!!erroSenha || criarUsuario.isPending}
                >
                  {criarUsuario.isPending ? 'Adicionando…' : 'Adicionar'}
                </button>
              </ModalRodape>
            </Form>
          )}
        </Modal>
      )}
    </>
  );
}
